import * as PIXI from 'pixi.js';
import { Settings } from '../Settings';
import { LogGroup, Rect, Safe, Scale, Size } from '../MyHelpers';
import { ResourceIdentifier } from '../ResourceManager/ResourceIdentifier';
import { IReadOnlyResourceManager, IWriteOnlyResourceManager } from '../ResourceManager/ResourceManager';
import { SpriteResource } from '../ResourceManager/SpriteResource';
import { FixedSizeFitContainer, ResponsiveSizeContainer, ResponsiveView } from '../../singlegame/ResponsiveHelpers';
export abstract class FountainParticlePIXI {
	private viewSize: PIXI.Rectangle;
	private source: PIXI.Point;

	public position: PIXI.Point;
	public velocity: PIXI.Point;
	public acceleration: PIXI.Point;

	public angle: number = 0;
	public angleVelocity: number = 0;
	public active: boolean = false;
	public created: number = 0;
	public lastMotionUpdate: number = 0;

	constructor(viewSize: PIXI.Rectangle, particleSize: PIXI.Rectangle) {
		this.viewSize = viewSize;
		this.source = new PIXI.Point(this.viewSize.width / 2, this.viewSize.height); // Start from center-bottom
		this.position = new PIXI.Point(0, 0);
		this.velocity = new PIXI.Point(0, 0);
		this.acceleration = new PIXI.Point(0, 0);
		this.angleVelocity = FountainPIXI.rand(-2, 2);  // Add random angle velocity
		this.angle = FountainPIXI.rand(0, 360);         // Random initial angle
		this.created = Date.now();
	}

	// Shoot particles upwards and with a slight horizontal spread
	Shoot(acceleration: number): void {
		this.active = true;
		this.position.x = this.source.x + FountainPIXI.rand(-50, 50);  // Slight horizontal variation
		this.position.y = this.source.y; // Start from the bottom
		this.acceleration.y = acceleration;  // Gravity pulling down
		let angle = FountainPIXI.rand(-Math.PI / 16, Math.PI / 16) - Math.PI / 2;
		let velocityMagnitude = FountainPIXI.rand(this.viewSize.height / 15, this.viewSize.height / 10);
		this.velocity.x = Math.cos(angle) * velocityMagnitude;
		this.velocity.y = Math.sin(angle) * velocityMagnitude;
		this.angle = FountainPIXI.rand(0, 360); // Assign a random starting angle for each particle
		this.angleVelocity = FountainPIXI.rand(-2, 2); // Random angular velocity for rotation
		this.lastMotionUpdate = Date.now();
	}

	// Update particle motion for infinite flow with randomization
	public MotionUpdate(millis: number): void {
		let motionFactor = ((millis - this.lastMotionUpdate) / 1000) * 100;
		motionFactor *= FountainPIXI.rand(0.95, 1.05); // Introduce randomness in time update
		this.velocity.x += this.acceleration.x * motionFactor;
		this.velocity.y += this.acceleration.y * motionFactor;
		this.position.x += this.velocity.x * motionFactor;
		this.position.y += this.velocity.y * motionFactor;
		this.angle += this.angleVelocity * FountainPIXI.rand(0.95, 1.05);  // Apply continuous rotation with slight randomness
		if (this.position.y > this.viewSize.height || this.position.x < 0 || this.position.x > this.viewSize.width) {
			this.Shoot(this.acceleration.y);  // Re-shoot for infinite effect
		}
		this.lastMotionUpdate = millis;
	}

	public abstract Draw(container: PIXI.Container): void;
}
export class FountainParticleWithSpritePIXI extends FountainParticlePIXI {
	protected sprite: PIXI.AnimatedSprite;

	constructor(viewSize: PIXI.Rectangle, textures: PIXI.Texture[], spriteSize: PIXI.Rectangle) {
		super(viewSize, spriteSize);

		// Create the animated sprite
		this.sprite = new PIXI.AnimatedSprite(textures);

		// Set a random starting frame
		this.sprite.gotoAndStop(FountainPIXI.rand(0, textures.length - 1, true)); 
		
		// Set a random speed for the animation
		this.sprite.animationSpeed = FountainPIXI.rand(0.05, 0.15); // Random speed between 0.05 and 0.15
		
		this.sprite.play();  // Start the animation
		this.sprite.pivot.set(spriteSize.width / 2, spriteSize.height / 2);  // Set pivot for rotation
		this.sprite.scale.set(0.5);  // Scale down the coin sprite
	}

	// Override to draw and rotate the sprite
	public override Draw(container: PIXI.Container): void {
		this.sprite.x = this.position.x;
		this.sprite.y = this.position.y;
		this.sprite.rotation = this.angle * (Math.PI / 180);  // Rotate the coin
		if (!container.children.includes(this.sprite)) {
			container.addChild(this.sprite);  // Add the sprite to the container
		}
		
		// container.width = Scale.W(5000);
		// container.height = Scale.H(1080);
		// container.x = (Scale.W(1920) - container.width)/2;
		// container.y = Scale.H(1080);
	}

	public override MotionUpdate(millis: number) {
		super.MotionUpdate(millis);
		this.sprite.updateTransform();  // Ensure the animation continues during motion
	}
}


export class FountainPIXI {
	private viewSize: PIXI.Rectangle;
	private container: PIXI.Container = null; 
	private N_PARTICLES: number = 200;  // Number of particles (increase if needed)
	private particles: FountainParticleWithSpritePIXI[] = [];
	private spriteSize: PIXI.Rectangle;
	private running: boolean = false;
	private acceleration: number;
	private textures: PIXI.Texture[] = [];
	

	constructor(viewSize: PIXI.Rectangle, spriteSize: PIXI.Rectangle, acceleration: number) {
		this.viewSize = viewSize;
		this.spriteSize = spriteSize;
		this.acceleration = acceleration;

		

	}


	// Load resources asynchronously and prepare the particle system
	public async PrepareResources(resourceManager: IWriteOnlyResourceManager): Promise<void> {
        resourceManager.AddGlobal(new SpriteResource(Settings.ResourcesFolder + "multigame/slot/common/resources/coin_1_200x200", ResourceIdentifier.Global.Add("fountain").Add("coin"), true, LogGroup.Global.Add("fountain").Add("coin"), true));
	}

	// Once resources are loaded, initialize particles with animated textures
	protected spriteCoin: SpriteResource

	public async ResourcesLoaded(resourceManager: IReadOnlyResourceManager): Promise<void> {
		let spriteToGet = resourceManager.Get<SpriteResource>(ResourceIdentifier.Global.Add("fountain").Add("coin"));
		this.spriteCoin = spriteToGet;
	}

	protected fountainContainer: PIXI.Container = null;
	protected responsiveViewFountain: ResponsiveView;
	protected responsiveSizeContainerFountain: ResponsiveSizeContainer;
	protected fixedSizeContainerFountain: FixedSizeFitContainer;
	protected created: boolean = false;

	public Create(): void {

		this.responsiveViewFountain = new ResponsiveView(window.innerWidth, window.innerHeight, { color: 0x220000, name: 'fountain-responsive-view' });
		gameViewAppPixiResponsive.GetAppView().stage.addChild(this.responsiveViewFountain);

		this.responsiveSizeContainerFountain = new ResponsiveSizeContainer(0, 0.5, 1, 1, NaN, { color: 0x550000, secondaryColor: 0x005500, name: 'fountain-responsive-container' });
		this.responsiveViewFountain.addChild(this.responsiveSizeContainerFountain);

		this.fixedSizeContainerFountain = new FixedSizeFitContainer(Scale.W(1920), Scale.H(1080), { color: 0x550000, secondaryColor: 0x005500, name: 'fountain-fixed-container' });
		this.responsiveSizeContainerFountain.addChild(this.fixedSizeContainerFountain);

		this.container = new PIXI.Container();
		this.container.name = "coins-container";


		this.fountainContainer = new PIXI.Container();
		this.fountainContainer.name = "fountain-container";
		//this.fountainContainer.pivot.set(Scale.W(1920) / 2, 0);
		this.fountainContainer.addChild(this.container);
		this.fountainContainer.visible = false;

		this.fixedSizeContainerFountain.addChild(this.fountainContainer);		
		this.InternalCreate();
		this.OnResize();
		
	}



	protected InternalCreate(): void {
		for (let i = 0; i <= this.spriteCoin.sprite.NumFrames() - 1; i++) {
			let texture = PIXI.Texture.from(this.spriteCoin.sprite.GetDecodedFrame(i));
			this.textures.push(texture);  // Add all frames to the texture array
		}

		// Create particles using the array of textures for the animated sprite
		for (let i = 0; i < this.N_PARTICLES; i++) {
			this.particles.push(new FountainParticleWithSpritePIXI(
				this.viewSize,
				this.textures,
				this.spriteSize
			));
		}
		this.created = true;
		// this.CreateResponsiveContainers();
	}

	// protected responsiveView: ResponsiveView;
	// protected fountainContainer: FixedSizeContainer;

	// protected CreateResponsiveContainers(): void {
	// 	// this.responsiveView = new ResponsiveView(window.screen.availWidth, window.screen.availHeight, {color: 0x220000, name: 'fountain-responsive-view'});
		
	// 	// this.fountainContainer = new FixedSizeContainer(Scale.W(this.mainContainerSize.width), Scale.H(this.mainContainerSize.height), {color: 0xbb0000, name: 'fountain-fixed-container'});
	// 	// this.responsiveView.addChild(this.fountainContainer);
	// 	// this.mainContainer.addChild(this.container);

	// 	// this.fountainContainer.addChild(this.container);
	// 	// this.container.position.set(this.mainContainer.width/2 - this.container.width/2, this.mainContainer.height/2 - this.container.height/2);
	// 	// this.fountainContainer.position.set(window.screen.availWidth/2 - this.fountainContainer.width/2, window.screen.availHeight/2 - this.fountainContainer.height/2);
	// 	// this.container.position.set(Scale.W(this.mainContainerSize.width)/2 - this.container.width/2, window.screen.availHeight - Scale.H(this.mainContainerSize.height) )//Scale.H(this.mainConainerSize.height)/2 - this.container.height/2);

	// }

	static rand(_max: number, _min: number, _int: boolean = false): number {
		let max = (_max === 0 || _max) ? _max : 1;
		let min = _min || 0;
		let gen = min + (max - min) * Math.random();
		return (_int) ? Math.round(gen) : gen;
	}
	// Update the particle system every frame
	Update(): void {	
		if (this.running) {
			let millis: number = Date.now();
			this.container.removeChildren();  // Clear the container for the new frame
			for (let particle of this.particles) {
				particle.Draw(this.container);
				particle.MotionUpdate(millis);
			}
		}
	}

	public OnStartedCallback: OnFountainStartedCallback = null;
	public OnStoppedCallback: OnFountainStoppedCallback = null;
	// Start the fountain animation
	async Start(): Promise<void> {
		if (this.running) return;
		this.running = true;
		if (this.fountainContainer != null)
		{
			this.fountainContainer.visible = true;
		}

		for (let particle of this.particles) {
			particle.Shoot(this.acceleration);  // Initialize particle positions and velocities
		}
		this.OnStartedCallback?.();
	}

	// Stop the fountain animation
	Stop(): void {
		this.running = false;
	}

	// Return the container with the animated particles
	getContainer(): PIXI.Container {
		return null;
	}

	Destroy(): void {
		this.particles = [];
		this.running = false;
		this.InternalDestroy();
		this.OnStoppedCallback?.();
	}

	public OnResize():void{

		if (!this.created) return;

		const fountRect = new Rect(0,0,Scale.W(1920), Scale.H(1080))  

		const availableSize = new Size(this.responsiveViewFountain.availableWidth, this.responsiveViewFountain.availableHeight);

		this.responsiveViewFountain?.setAvailableSize(window.innerWidth, window.innerHeight);

		this.fountainContainer.position.x = (fountRect.width) / 4;
		this.fountainContainer.position.y = 0;//((availableSize.Height * this.responsiveSizeContainerFountain.percentHeight) - this.fixedSizeContainerFountain.fixedHeight) ;


	}

	protected InternalDestroy(): void {
		Safe(() => {this.responsiveViewFountain.destroy({children: true})});
		this.responsiveViewFountain = null;
		this.created = false;
	}
}
export type OnFountainStartedCallback = () => void;
export type OnFountainStoppedCallback = () => void;